// Minimal client for Ory Kratos self-service browser flows.
// We do NOT pull @ory/client to keep the bundle small. Only the operations the
// portal actually drives (login, registration, verify, logout) are implemented.

const KRATOS_URL = (import.meta.env.VITE_KRATOS_PUBLIC_URL as string) ?? 'http://localhost:4433';

export type UiNode = {
  type: string;
  group: string;
  attributes: {
    name?: string;
    type?: string;
    value?: unknown;
    required?: boolean;
    disabled?: boolean;
    node_type?: string;
  };
  meta?: { label?: { id?: number; text?: string } };
  messages?: UiMessage[];
};

export type UiMessage = {
  id: number;
  text: string;
  type: 'info' | 'error' | 'success';
};

export type Flow = {
  id: string;
  type: string;
  expires_at: string;
  ui: {
    action: string;
    method: string;
    nodes: UiNode[];
    messages?: UiMessage[];
  };
};

async function getJSON<T>(url: string): Promise<T> {
  const res = await fetch(url, { credentials: 'include', headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(`kratos ${res.status}`);
  return (await res.json()) as T;
}

async function postForm<T>(url: string, body: Record<string, unknown>): Promise<T> {
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  // Browser flows return 200 on continuation, 422 with location for redirects, 4xx with flow on validation.
  const text = await res.text();
  let parsed: unknown = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parsed = null;
  }
  if (!res.ok && res.status !== 422) {
    if (parsed && typeof parsed === 'object') return parsed as T;
    throw new Error(`kratos ${res.status}: ${text}`);
  }
  return parsed as T;
}

export function loginFlowURL(): string {
  return `${KRATOS_URL}/self-service/login/browser?return_to=${encodeURIComponent(window.location.origin + '/')}`;
}

export function registrationFlowURL(): string {
  return `${KRATOS_URL}/self-service/registration/browser?return_to=${encodeURIComponent(window.location.origin + '/')}`;
}

// Two-step logout. Hitting /self-service/logout/browser directly only returns
// a JSON envelope with the real, single-use logout URL — visiting it via an
// <a href> shows the JSON instead of clearing the session. Fetch the envelope
// first, then navigate to the URL it gives us.
export async function logout(): Promise<void> {
  type LogoutFlow = { logout_url: string; logout_token: string };
  const flow = await getJSON<LogoutFlow>(`${KRATOS_URL}/self-service/logout/browser`);
  window.location.href = flow.logout_url;
}

export async function fetchLoginFlow(id: string): Promise<Flow> {
  return getJSON<Flow>(`${KRATOS_URL}/self-service/login/flows?id=${encodeURIComponent(id)}`);
}

export async function fetchRegistrationFlow(id: string): Promise<Flow> {
  return getJSON<Flow>(`${KRATOS_URL}/self-service/registration/flows?id=${encodeURIComponent(id)}`);
}

export async function submitFlow<T = Flow>(action: string, body: Record<string, unknown>): Promise<T> {
  return postForm<T>(action, body);
}

export type SessionInfo = {
  active: boolean;
  identity: { id: string; traits: { email: string; name?: string } };
};

export async function whoami(): Promise<SessionInfo | null> {
  try {
    return await getJSON<SessionInfo>(`${KRATOS_URL}/sessions/whoami`);
  } catch {
    return null;
  }
}

export function csrfTokenFromFlow(flow: Flow): string | undefined {
  const node = flow.ui.nodes.find((n) => n.attributes.name === 'csrf_token');
  return node?.attributes.value as string | undefined;
}

export function flowHasNode(flow: Flow, name: string): boolean {
  return flow.ui.nodes.some((n) => n.attributes.name === name);
}

export function flowMessages(flow: Flow): UiMessage[] {
  const top = flow.ui.messages ?? [];
  const fields = flow.ui.nodes.flatMap((n) => n.messages ?? []);
  return [...top, ...fields];
}
